<template>
    <section class="backlog-insights-section">
        <section
            aria-labelledby="summaries-heading"
            class="summaries-wrapper"
        >
            <h2
                id="summaries-heading"
                class="visually-hidden"
            >
                Summaries
            </h2>
            <BacklogSummaries
                title=""
                :backlog-summaries-data="backlogSummariesData"
            />
        </section>

        <section
            aria-labelledby="interval-charts-heading"
            class="charts-container"
        >
            <h2
                id="interval-charts-heading"
                class="visually-hidden"
            >
                Interval Charts
            </h2>
            <OperationsInsightsChart
                :title="`Backlog Age by (${interval})`"
                :chart-options="ageBandChartOptions"
                :is-loading="isLoading"
                :error="blAgeBandsBucketsError"
            />
            <OperationsInsightsChart
                :title="`Backlog In / Out by (${interval})`"
                :chart-options="inOutChartOptions"
                :is-loading="isLoading"
                :error="backlogError"
            />
        </section>
        <section class="grid-container-wrapper">
            <div class="grid-container">
                <OperationsInsightsGrid
                    :title="`Total Backlog by (${interval})`"
                    :column-defs="backlogTotalByIntervalColumnDefs"
                    :row-data="backlogTotalByIntervalGridData"
                    :error="backlogError"
                />
            </div>
        </section>
    </section>
</template>

<script setup lang="ts">
import BacklogSummaries from '@/pages/OperationsInsights/components/BacklogSummaries.vue';
import OperationsInsightsChart from '@/pages/OperationsInsights/components/OperationsInsightsChart.vue';
import OperationsInsightsGrid from '@/pages/OperationsInsights/components/OperationsInsightsGrid.vue';
import { toRef } from 'vue';
import { useBLMetricsData } from '@/pages/OperationsInsights/composables/useBLMetricsData';
import { useBLAgeBandsBucketsData } from '@/pages/OperationsInsights/composables/useBLAgeBandsBucketsData';
import { useBLOverviewChartConfigs } from '@/pages/OperationsInsights/composables/useBLOverviewChartConfigs';
import { ApiQueryParams } from '@/pages/OperationsInsights/types/ApiQueryParams';
import { useCombinedStates } from '@/pages/OperationsInsights/composables/useCombinedStates';

type Props = {
    queryParams: ApiQueryParams;
};

const props = defineProps<Props>();
const queryParamsRef = toRef(props, 'queryParams');

// Data composables
const blMetricsData = useBLMetricsData(queryParamsRef);
const {
    interval,
    backlogSummariesData,
    backlogMetricsChartSeries,
    backlogTotalByIntervalGridData,
    backlogTotalByIntervalColumnDefs,
    error: backlogError
} = blMetricsData;

const blAgeBandsBucketsData = useBLAgeBandsBucketsData(queryParamsRef);
const {
    ageBandChartSeries,
    error: blAgeBandsBucketsError
} = blAgeBandsBucketsData;

// Chart configuration
const { ageBandChartOptions, inOutChartOptions } = useBLOverviewChartConfigs(
    ageBandChartSeries,
    backlogMetricsChartSeries
);

// Combined states
const { isLoading } = useCombinedStates(blMetricsData, blAgeBandsBucketsData);
</script>

<style scoped lang="scss">
@use '../styles/metric-card.scss';
@use '../styles/backlog-insights-layout.scss';
</style>
